import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  MapPin, ChevronRight, Search, ArrowLeft, Phone, Mail, Globe,
  X, Building2, Tag, CalendarDays, GraduationCap, Briefcase, Home, Map, Leaf,
  Star, Heart, Recycle, Award, Users, ShoppingBag, Sparkles, Clock
} from 'lucide-react';
import { 
  OFFICES, SERVICES, PRODUCTS, COURSES, CATEGORIES, EVENTS, RENTALS,
  getCommunityById, getCommunityOffices, getCommunityProducts, getCommunityServices,
  getActiveCommunities 
} from '../data/database';
import { 
  OfficesSection, 
  AppointmentServicesSection, 
  RentalsSection, 
  UpcomingEventsSection,
  ProductsSection, 
  CoursesSection 
} from '../components/HomeSections';
import BottomNav from '../components/BottomNav';
import { useI18n } from '../i18n';

// Placeholder logo component for communities without a logo
const PlaceholderLogo = ({ name, theme }) => {
  const gradientClass = theme?.gradient || 'from-emerald-500 to-green-600';
  return (
    <div className={`w-full h-full bg-gradient-to-br ${gradientClass} rounded-xl flex items-center justify-center`}>
      <div className="text-center text-white">
        <Leaf className="w-12 h-12 mx-auto mb-1" />
        <span className="text-xs font-bold leading-tight block">{name?.split(' ')[0]}</span>
      </div>
    </div>
  );
};

// Badge icon mapping
const BadgeIcon = ({ icon, className }) => {
  const icons = {
    leaf: Leaf,
    heart: Heart,
    recycle: Recycle,
    award: Award,
    utensils: Tag,
    star: Star,
  };
  const Icon = icons[icon] || Leaf;
  return <Icon className={className} />;
};

const CommunityPage = ({ communityId = 'la-biografica', onNavigate, onBack }) => {
  const { t, tc } = useI18n();
  
  // Get community data from database
  const community = useMemo(() => getCommunityById(communityId), [communityId]);
  const theme = community?.theme || { gradient: 'from-emerald-500 to-green-600', accent: 'emerald' };
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('offices');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [showExploreDialog, setShowExploreDialog] = useState(false);
  const searchRef = useRef(null);

  // Get REAL data filtered by community
  const communityOffices = useMemo(() => getCommunityOffices(communityId), [communityId]);
  const communityServices = useMemo(() => getCommunityServices(communityId), [communityId]);
  const communityProducts = useMemo(() => getCommunityProducts(communityId), [communityId]);
  const communityRentals = useMemo(() => {
    if (!community) return [];
    return (RENTALS || []).filter(r => community.officeIds?.includes(r.officeId));
  }, [community]);
  const communityCourses = useMemo(() => {
    if (!community) return [];
    return COURSES.filter(c => community.officeIds?.includes(c.officeId));
  }, [community]);
  const communityEvents = useMemo(() => {
    if (!community) return [];
    return (EVENTS || []).filter(e => community.officeIds?.includes(e.officeId));
  }, [community]);

  // Categories for this community - with icons for display
  const COMMUNITY_CATEGORIES = [
    { id: 'all', name: 'Tutti', icon: Sparkles, color: 'bg-gray-100', groups: null },
    { id: 'wellness', name: 'Benessere', icon: Heart, color: 'bg-pink-100', groups: ['bellezza', 'salute'] },
    { id: 'fitness', name: 'Fitness', icon: Users, color: 'bg-orange-100', groups: ['sport'] },
    { id: 'natura', name: 'Natura', icon: Leaf, color: 'bg-green-100', groups: ['natura', 'alimentari'] },
    { id: 'prodotti', name: 'Prodotti', icon: ShoppingBag, color: 'bg-purple-100', groups: ['shopping'] },
    { id: 'servizi', name: 'Servizi', icon: Briefcase, color: 'bg-blue-100', groups: ['other'] },
  ];
  
  const categories = COMMUNITY_CATEGORIES;

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter by category and search
  const getCategoryGroup = (business) => {
    const cat = business.categorySlug || '';
    if (['parrucchieri', 'estetisti', 'barbieri', 'centri-benessere', 'massaggiatori'].includes(cat)) return 'bellezza';
    if (['medici', 'dentisti', 'fisioterapisti', 'farmacie', 'ottici'].includes(cat)) return 'salute';
    if (['palestre', 'piscine', 'personal-trainer', 'yoga-pilates'].includes(cat)) return 'sport';
    if (['ristoranti', 'pizzerie', 'bar-caffe', 'pasticcerie', 'gelaterie'].includes(cat)) return 'food';
    if (['bio', 'biologico', 'naturale', 'erboristeria'].includes(cat)) return 'natura';
    if (['alimentari', 'supermercati', 'gastronomia'].includes(cat)) return 'alimentari';
    return 'other';
  };

  const filteredOffices = useMemo(() => {
    let result = communityOffices;
    if (selectedCategory !== 'all') {
      const catConfig = categories.find(c => c.id === selectedCategory);
      if (catConfig?.groups) {
        result = result.filter(b => catConfig.groups.includes(getCategoryGroup(b)));
      }
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(b => b.name.toLowerCase().includes(q) || b.category?.toLowerCase().includes(q));
    }
    return result;
  }, [communityOffices, selectedCategory, searchQuery, categories]);

  const filteredServices = useMemo(() => {
    let result = communityServices;
    if (selectedCategory !== 'all') {
      const businessIds = filteredOffices.map(b => b.id);
      result = result.filter(s => businessIds.includes(s.officeId));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(q));
    }
    return result;
  }, [communityServices, filteredOffices, selectedCategory, searchQuery]);

  const filteredRentals = useMemo(() => {
    let result = communityRentals;
    if (selectedCategory !== 'all') {
      const businessIds = filteredOffices.map(b => b.id);
      result = result.filter(r => businessIds.includes(r.officeId));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r => r.name.toLowerCase().includes(q));
    }
    return result;
  }, [communityRentals, filteredOffices, selectedCategory, searchQuery]);

  const filteredProducts = useMemo(() => {
    let result = communityProducts;
    if (selectedCategory !== 'all') {
      const businessIds = filteredOffices.map(b => b.id);
      result = result.filter(p => businessIds.includes(p.officeId));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q));
    }
    return result;
  }, [communityProducts, filteredOffices, selectedCategory, searchQuery]);

  const filteredEvents = useMemo(() => {
    let result = communityEvents;
    if (selectedCategory !== 'all') {
      const businessIds = filteredOffices.map(b => b.id);
      result = result.filter(e => businessIds.includes(e.officeId));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(e => e.name.toLowerCase().includes(q));
    }
    return result;
  }, [communityEvents, filteredOffices, selectedCategory, searchQuery]);

  const filteredCourses = useMemo(() => {
    let result = communityCourses;
    if (selectedCategory !== 'all') {
      const businessIds = filteredOffices.map(b => b.id);
      result = result.filter(c => businessIds.includes(c.officeId));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c => c.name.toLowerCase().includes(q));
    }
    return result;
  }, [communityCourses, filteredOffices, selectedCategory, searchQuery]);

  // Search suggestions from REAL data
  const searchSuggestions = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    const query = searchQuery.toLowerCase();
    const suggestions = [];
    
    communityOffices.filter(b => b.name.toLowerCase().includes(query)).slice(0, 3).forEach(b => {
      suggestions.push({ type: 'business', id: b.id, name: b.name, subtitle: b.category, icon: Building2 });
    });
    communityServices.filter(s => s.name.toLowerCase().includes(query)).slice(0, 3).forEach(s => {
      const biz = OFFICES.find(b => b.id === s.officeId);
      suggestions.push({ type: 'service', id: s.id, name: s.name, subtitle: `€${s.price} • ${biz?.name || ''}`, icon: Briefcase });
    });
    communityProducts.filter(p => p.name.toLowerCase().includes(query)).slice(0, 2).forEach(p => {
      const biz = OFFICES.find(b => b.id === p.officeId);
      suggestions.push({ type: 'product', id: p.id, name: p.name, subtitle: `€${p.price} • ${biz?.name || ''}`, icon: Tag });
    });
    communityEvents.filter(e => e.name.toLowerCase().includes(query)).slice(0, 2).forEach(e => {
      suggestions.push({ type: 'event', id: e.id, name: e.name, subtitle: new Date(e.date).toLocaleDateString('it-IT'), icon: CalendarDays });
    });
    communityCourses.filter(c => c.name.toLowerCase().includes(query)).slice(0, 2).forEach(c => {
      suggestions.push({ type: 'course', id: c.id, name: c.name, subtitle: `€${c.price} • ${c.totalHours} ore`, icon: GraduationCap });
    });
    
    return suggestions.slice(0, 8);
  }, [searchQuery, communityOffices, communityServices, communityProducts, communityEvents, communityCourses]);

  const handleSuggestionClick = (suggestion) => {
    setShowSuggestions(false);
    setSearchQuery('');
    onNavigate(suggestion.type, { id: suggestion.id });
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSearchQuery('');
  };

  const hasActiveFilters = selectedCategory !== 'all' || searchQuery !== '';

  // Stats
  const stats = {
    offices: communityOffices.length,
    services: communityServices.length,
    products: communityProducts.length,
    events: communityEvents.length
  };

  // Navigation wrapper that maps to correct detail page routes
  const handleNavigate = (type, params) => {
    const routeMap = {
      'business': 'business',
      'service': 'service-detail',
      'product': 'product-detail',
      'event': 'event-detail',
      'course': 'course-detail'
    };
    onNavigate(routeMap[type] || type, params);
  };

  // Dynamic theme classes
  const themeGradient = theme.gradient || 'from-cyan-600 via-cyan-500 to-teal-500';
  const themeAccent = theme.accent || 'cyan-500';
  const themeBadge = theme.badge || 'bg-cyan-100 text-cyan-700';
  const themeButton = theme.button || 'bg-cyan-500 hover:bg-cyan-600';

  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-0">
      {/* Hero Section */}
      <div className={`relative bg-gradient-to-br ${themeGradient} overflow-hidden`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        </div>
        
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#f9fafb"/>
          </svg>
        </div>

        <div className="relative max-w-screen-2xl mx-auto px-6 pt-6 pb-24 lg:pt-10 lg:pb-32">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-8">
            <button onClick={onBack || (() => window.history.back())} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium hidden sm:inline">{t('misc', 'backToSkipres')}</span>
            </button>
            <div className="flex items-center gap-2 text-white/80">
              <span className="text-xs sm:text-sm">Powered by</span>
              <span className="font-bold text-white">Skipres</span>
            </div>
          </div>

          {/* Hero content */}
          <div className="text-center max-w-4xl mx-auto">
            <div className="w-28 h-28 lg:w-36 lg:h-36 bg-white rounded-2xl shadow-xl p-3 mx-auto mb-6 overflow-hidden">
              {community.logo ? (
                <img src={community.logo} alt={community.name} className="w-full h-full object-contain" />
              ) : (
                <PlaceholderLogo name={community.name} theme={theme} />
              )}
            </div>
            <h1 className="text-3xl lg:text-5xl font-bold text-white mb-2">{community.name}</h1>
            <p className="text-lg lg:text-xl text-white/90 font-medium mb-4">{community.subtitle}</p>
            <p className="text-white/80 text-sm lg:text-base max-w-2xl mx-auto mb-8">{community.description}</p>

            <div className="flex flex-wrap justify-center gap-3 lg:gap-6 mb-10">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2.5">
                <span className="block text-xl lg:text-2xl font-bold text-white">{stats.offices}</span>
                <span className="text-xs text-white/80">Attività</span>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2.5">
                <span className="block text-xl lg:text-2xl font-bold text-white">{stats.services}</span>
                <span className="text-xs text-white/80">Servizi</span>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2.5">
                <span className="block text-xl lg:text-2xl font-bold text-white">{stats.products}</span>
                <span className="text-xs text-white/80">Prodotti</span>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2.5">
                <span className="block text-xl lg:text-2xl font-bold text-white">{stats.events}</span>
                <span className="text-xs text-white/80">Eventi</span>
              </div>
            </div>

            {/* Search Bar */}
            <div className="relative z-[200] max-w-3xl mx-auto" ref={searchRef}>
              <div className={`flex items-center bg-white rounded-full shadow-2xl transition-all ${searchFocused ? 'ring-4 ring-white/30' : ''}`}>
                <div className="flex-1 relative">
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => { setSearchFocused(true); setShowSuggestions(true); }}
                    placeholder={t('community', 'searchInCommunity')}
                    className="w-full pl-14 pr-6 py-4 lg:py-5 text-gray-900 text-base lg:text-lg placeholder-gray-400 outline-none rounded-full"
                    data-testid="community-search-input"
                  />
                </div>
                <button className={`hidden lg:flex items-center gap-2 ${themeButton} text-white px-6 py-3 rounded-full mr-2 transition-colors font-medium`}>
                  <Search className="w-5 h-5" />
                  Cerca
                </button>
              </div>

              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-[200]">
                  <div className="p-2">
                    {searchSuggestions.map((suggestion) => (
                      <button
                        key={`${suggestion.type}-${suggestion.id}`}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors text-left"
                      >
                        <div className={`w-10 h-10 ${themeBadge} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <suggestion.icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{suggestion.name}</p>
                          <p className="text-sm text-gray-500 truncate">{suggestion.subtitle}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-screen-2xl mx-auto px-6 -mt-6 relative">
        
        {/* Categories */}
        <div className="bg-white rounded-2xl shadow-sm p-4 lg:p-6 mb-8 relative z-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg lg:text-xl font-bold text-gray-900">Esplora per Categoria</h2>
            {hasActiveFilters && (
              <button onClick={clearFilters} className={`text-sm text-${themeAccent} hover:opacity-80 font-medium flex items-center gap-1`}>
                <X className="w-4 h-4" /> Rimuovi filtri
              </button>
            )}
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map(cat => {
              const IconComponent = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat.id)}
                  className={`flex flex-col items-center gap-2 p-3 lg:p-4 rounded-xl transition-all flex-shrink-0 min-w-[80px] lg:min-w-[100px] ${
                    selectedCategory === cat.id 
                      ? `${themeButton} text-white shadow-lg scale-105` 
                      : 'hover:bg-gray-50'
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  <div className={`w-12 h-12 lg:w-14 lg:h-14 rounded-xl flex items-center justify-center transition-colors ${
                    selectedCategory === cat.id ? 'bg-white/20' : cat.color
                  }`}>
                    <IconComponent className={`w-6 h-6 lg:w-7 lg:h-7 ${selectedCategory === cat.id ? 'text-white' : ''}`} />
                  </div>
                  <span className={`text-xs lg:text-sm font-medium text-center ${selectedCategory === cat.id ? 'text-white' : 'text-gray-700'}`}>
                    {cat.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="mb-6 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-500">Filtri attivi:</span>
            {selectedCategory !== 'all' && (
              <span className={`${themeBadge} px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1`}>
                {categories.find(c => c.id === selectedCategory)?.name}
                <button onClick={() => setSelectedCategory('all')}><X className="w-3 h-3" /></button>
              </span>
            )}
            {searchQuery && (
              <span className={`${themeBadge} px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1`}>
                &quot;{searchQuery}&quot;
                <button onClick={() => setSearchQuery('')}><X className="w-3 h-3" /></button>
              </span>
            )}
          </div>
        )}

        {/* Using shared components from HomeSections - Stile Homepage Aggiornata */}
        <OfficesSection 
          offices={filteredOffices}
          title="Le nostre attività"
          onNavigate={handleNavigate}
          showViewAll={false}
        />

        {/* Servizi ad Appuntamento - Prenotazioni Orarie */}
        <AppointmentServicesSection 
          services={filteredServices.slice(0, 12)}
          title="Servizi ad Appuntamento"
          badge="Prenotazioni orarie"
          onNavigate={handleNavigate}
          showViewAll={false}
        />

        {/* Noleggi e Soggiorni - Prenotazioni Giornaliere */}
        <RentalsSection 
          rentals={filteredRentals.slice(0, 8)}
          title="Noleggi e Soggiorni"
          badge="Prenotazioni giornaliere"
          onNavigate={handleNavigate}
          showViewAll={false}
        />

        {/* Prossimi Eventi */}
        <UpcomingEventsSection 
          events={filteredEvents.slice(0, 6)}
          title="Prossimi Eventi"
          badge="Esperienze uniche"
          onNavigate={handleNavigate}
          showViewAll={false}
        />

        <ProductsSection 
          products={filteredProducts.slice(0, 12)}
          title="I nostri prodotti"
          onNavigate={handleNavigate}
          showViewAll={false}
        />

        <CoursesSection 
          courses={filteredCourses.slice(0, 8)}
          title="Corsi disponibili"
          onNavigate={handleNavigate}
          showViewAll={false}
        />

        {/* No results */}
        {hasActiveFilters && filteredOffices.length === 0 && filteredServices.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center mb-8">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('community', 'noResultsInCommunity')}</h3>
            <p className="text-gray-500 mb-4">Prova a modificare i filtri o la ricerca</p>
            <button onClick={clearFilters} className={`${themeButton} text-white px-6 py-2 rounded-full font-medium transition-colors`}>
              Rimuovi filtri
            </button>
          </div>
        )}

        {/* Contact Banner */}
        <div className="relative overflow-hidden rounded-2xl mb-8">
          <div className={`absolute inset-0 bg-gradient-to-r ${themeGradient}`} />
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
          </div>
          <div className="relative p-6 lg:p-10 flex flex-col lg:flex-row items-center gap-6 lg:gap-10">
            <div className="flex-shrink-0 w-20 h-20 lg:w-28 lg:h-28 bg-white rounded-2xl p-2 shadow-lg overflow-hidden">
              {community.logo ? (
                <img src={community.logo} alt={community.name} className="w-full h-full object-contain" />
              ) : (
                <PlaceholderLogo name={community.name} theme={theme} />
              )}
            </div>
            <div className="flex-1 text-center lg:text-left text-white">
              <h3 className="text-xl lg:text-2xl font-bold mb-2">Scopri {community.name}</h3>
              <p className="text-white/80 text-sm lg:text-base mb-4">
                {stats.offices} attività ti aspettano con servizi, prodotti ed eventi esclusivi.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm">
                <a href={`tel:${community.phone}`} className="flex items-center gap-2 hover:text-white/80 transition-colors">
                  <Phone className="w-4 h-4" /> {community.phone}
                </a>
                <a href={`mailto:${community.email}`} className="flex items-center gap-2 hover:text-white/80 transition-colors">
                  <Mail className="w-4 h-4" /> {community.email}
                </a>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <a 
                href={`https://${community.website}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`flex items-center justify-center gap-2 bg-white text-gray-800 px-6 py-3 rounded-full font-semibold hover:bg-gray-50 transition-colors`}
              >
                <Globe className="w-4 h-4" /> {t('community', 'visitWebsite')}
              </a>
              <div className="flex items-center gap-2 text-white/70 text-xs justify-center">
                <MapPin className="w-3 h-3" /> {community.address}
              </div>
            </div>
          </div>
        </div>

        {/* Skipres Branding */}
        <div className="text-center py-6 border-t border-gray-200 mb-20 lg:mb-0">
          <p className="text-sm text-gray-500">Questa community è gestita tramite <span className="font-semibold text-gray-700">Skipres</span></p>
          <p className="text-xs text-gray-400 mt-1">{t('misc', 'platform')}</p>
        </div>
      </div>

      {/* Explore Dialog Modal */}
      {showExploreDialog && (
        <div className="fixed inset-0 z-[400] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 animate-slide-up">
            <div className="text-center mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('community', 'whereToGo')}</h3>
              <p className="text-sm text-gray-500">{t('community', 'chooseWhatToExplore')}</p>
            </div>
            
            <div className="space-y-3">
              {/* Stay in Community */}
              <button
                onClick={() => {
                  setShowExploreDialog(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`w-full flex items-center gap-4 p-4 ${themeBadge} border-2 border-current rounded-xl hover:opacity-80 transition-colors`}
              >
                <div className={`w-12 h-12 ${themeButton} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <Map className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">{community.name}</p>
                  <p className="text-sm text-gray-500">{t('community', 'stayInCommunity')}</p>
                </div>
              </button>

              {/* Go to Skipres Home */}
              <button
                onClick={() => {
                  setShowExploreDialog(false);
                  if (onBack) onBack();
                  else window.history.back();
                }}
                className="w-full flex items-center gap-4 p-4 bg-gray-50 border-2 border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Home className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">Skipres</p>
                  <p className="text-sm text-gray-500">{t('community', 'exploreAllBusinesses')}</p>
                </div>
              </button>
            </div>

            <button
              onClick={() => setShowExploreDialog(false)}
              className="w-full mt-4 py-3 text-gray-500 font-medium hover:text-gray-700 transition-colors"
            >
              {t('common', 'cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Bottom Nav - Mobile only */}
      <div className="lg:hidden">
        <BottomNav 
          activeTab="explore" 
          setActiveTab={(tabId) => {
            // Map tab IDs to navigation routes
            const tabRoutes = {
              'explore': () => setShowExploreDialog(true), // Show dialog instead of navigating directly
              'calendar': () => onNavigate('calendar', {}),
              'orders': () => onNavigate('orders', {}),
              'profile': () => onNavigate('menu', {})
            };
            if (tabRoutes[tabId]) {
              tabRoutes[tabId]();
            }
          }}
          onSearch={() => {
            const searchInput = document.querySelector('[data-testid="community-search-input"]');
            if (searchInput) {
              searchInput.focus();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
          stayOnPageAfterAuth={true}
        />
      </div>
    </div>
  );
};

export default CommunityPage;
